import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Brain, Globe, Sparkles, LogIn, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useUIStore, useAuthStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();
  const { language } = useUIStore();
  const { isAuthenticated, login } = useAuthStore();
  const { t } = useTranslation(language);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/app');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/app');
    } catch (error) {
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">OSLO One</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">Internal Use Only</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.section
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="pt-32 pb-20 px-6"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                AI-First Project Management
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-tight"
            >
              {t('hero_title')}
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg md:text-xl text-muted-foreground max-w-lg"
            >
              {t('hero_subtitle')}
            </motion.p>
          </div>

          {/* Right - Login Panel */}
          <motion.div
            variants={itemVariants}
            className="relative"
          >
            <div className="max-w-md mx-auto relative">
              {/* Glowing background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-accent/20 rounded-3xl blur-3xl" />
              
              {/* Login card */}
              <div className="relative glass rounded-3xl p-8">
                <div className="text-center mb-8">
                  <div className="h-16 w-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <LogIn className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold mb-2">Welcome Back</h2>
                  <p className="text-muted-foreground">Use your credentials to login</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-secondary/50"
                      data-testid="login-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-secondary/50"
                      data-testid="login-password"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full glow-primary py-6 text-lg"
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <LogIn className="mr-2 h-5 w-5" />
                        Sign In
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-center text-muted-foreground mt-6">
                  Contact your administrator if you need access
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Proof Section */}
      <motion.section
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="py-20 px-6"
      >
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
          {/* Feature 1 */}
          <motion.div
            variants={itemVariants}
            className="glass rounded-2xl p-8 card-hover"
          >
            <div className="h-14 w-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
              <Brain className="h-7 w-7 text-primary" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">
              {t('proof_1_title')}
            </h3>
            <p className="text-muted-foreground">
              {t('proof_1_desc')}
            </p>
          </motion.div>

          {/* Feature 2 */}
          <motion.div
            variants={itemVariants}
            className="glass rounded-2xl p-8 card-hover"
          >
            <div className="h-14 w-14 rounded-xl bg-accent/20 flex items-center justify-center mb-6">
              <Globe className="h-7 w-7 text-accent" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">
              {t('proof_2_title')}
            </h3>
            <p className="text-muted-foreground">
              {t('proof_2_desc')}
            </p>
          </motion.div>

          {/* Feature 3 */}
          <motion.div
            variants={itemVariants}
            className="glass rounded-2xl p-8 card-hover"
          >
            <div className="h-14 w-14 rounded-xl bg-chart-3/20 flex items-center justify-center mb-6">
              <Mic className="h-7 w-7 text-chart-3" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">
              {t('proof_3_title')}
            </h3>
            <p className="text-muted-foreground">
              {t('proof_3_desc')}
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-heading font-semibold">OSLO One</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 OSLO One. AI-First Project Management.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
